import React, { lazy, Suspense, useMemo } from 'react';
import { Spinner } from '@leagueplatform/genesis-core';
import { FormFieldWithTrailingAction } from '@web-config-app/core-react-ui';
import type {
  ControlComponent,
  EntityFormControlProps,
} from '@web-config-app/entity-form';
import { EntityFormControl } from '@web-config-app/entity-form';
import { CODE_EDITOR_LANGUAGES, CodeEditorOptions } from '@web-config-app/core';

// Lazy load CodeEditor to defer ~2-3MB Monaco bundle
const CodeEditor = lazy(() =>
  import('@web-config-app/code-editor').then((m) => ({
    default: m.CodeEditor,
  })),
);

export const DefaultEditorOptions = {
  enableMinimap: true,
  tabSize: 2,
  wordWrap: 'on' as const,
  scrollBeyondLastLine: false,
  lineNumbers: 'on' as const,
  folding: true,
  automaticLayout: true,
  height: 'auto',
  fontSize: 14,
  quickSuggestions: true,
  language: 'plaintext' as const,
} satisfies CodeEditorOptions & Record<string, unknown>;

export const CodeEditorControlContents = ({
  data = '',
  handleChange,
  path,
  enabled,
  id,
  label,
  hint,
  required,
  banner,
  statusMessage,
  inputStatus,
  schema,
}: EntityFormControlProps) => {
  const { editorProps, editorOptions, editorSchemas } = useMemo(() => {
    const schemaEditorOptions =
      schema?.['x-entity-control']?.options?.codeEditor ?? {};

    // Schema options are received as a flattened object, so we need to
    // extract specific properties to pass as props to the CodeEditor component
    const {
      language,
      height,
      enableMinimap,
      schema: editorSchemaOptions,
      ...options
    } = {
      ...DefaultEditorOptions,
      ...schemaEditorOptions,
    };

    const derivedLanguage = CODE_EDITOR_LANGUAGES.includes(language)
      ? language
      : 'plaintext';

    return {
      editorProps: { language: derivedLanguage, height },
      editorOptions: {
        readOnly: !enabled,
        minimap: { enabled: enableMinimap },
        ...options,
      },
      editorSchemas: editorSchemaOptions,
    };
  }, [enabled, schema]);

  return (
    <FormFieldWithTrailingAction
      id={id}
      label={label}
      hint={hint}
      required={required}
      banner={banner}
      inputStatus={inputStatus}
      statusMessage={statusMessage}
    >
      <div data-testid="code-editor-wrapper">
        <Suspense fallback={<Spinner />}>
          <CodeEditor
            value={data}
            onChange={(value) => handleChange(path, value || undefined)}
            options={editorOptions}
            schemas={editorSchemas}
            {...editorProps}
          />
        </Suspense>
      </div>
    </FormFieldWithTrailingAction>
  );
};

export const CodeEditorControl: ControlComponent = (props) => (
  <EntityFormControl
    {...props}
    renderControl={CodeEditorControlContents}
    omitWrapperComponent
  />
);
